import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchCategories, fetchAllTags, createService, createProduct } from '../services/api';
import CategorySelect from '../components/CategorySelect';

const AddListing = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [categories, setCategories] = useState([]);
  const [allTags, setAllTags] = useState([]); // For the new tag field
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  // --- New State for Improved Fields ---
  const [selectedTags, setSelectedTags] = useState([]);
  const [tagSearch, setTagSearch] = useState('');
  const [filteredTags, setFilteredTags] = useState([]);
  const [workingDays, setWorkingDays] = useState(['Mon', 'Tue', 'Wed', 'Thu', 'Fri']);
  const [timeRange, setTimeRange] = useState({ start: '09:00', end: '18:00' });

  const [formData, setFormData] = useState({
    listingType: 'service',
    category: '',
    serviceName: '',
    description: '',
    price: '',
    pricing_type: 'fixed', // New field for pricing strategy
    areasServed: '',
  });

  const daysList = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  useEffect(() => {
    const userData = localStorage.getItem('user');
    if (!userData) { navigate('/auth'); return; }
    try {
      const parsed = JSON.parse(userData);
      if (parsed.user_type !== 'vendor') { navigate('/'); return; }
      setUser(parsed);
      loadData(parsed);
    } catch (err) { navigate('/auth'); }
  }, [navigate]);

  const loadData = async (parsedUser) => {
    try {
      setLoading(true);
      const [cats, tgs] = await Promise.all([
        fetchCategories(), fetchAllTags()
      ]);
      setCategories(cats || []);
      setAllTags(tgs || []);
    } catch (err) {
      setError('Failed to load form data');
    } finally {
      setLoading(false);
    }
  };

  // --- Tag filtering effect ---
  useEffect(() => {
    if (tagSearch.trim().length > 0) {
      const search = tagSearch.toLowerCase();
      const filtered = allTags.filter(tag =>
        tag.name.toLowerCase().includes(search) &&
        !selectedTags.some(selected => selected.id === tag.id)
      );
      setFilteredTags(filtered.slice(0, 8));
    } else {
      setFilteredTags([]);
    }
  }, [tagSearch, allTags, selectedTags]);

  // --- Helper Functions ---
  const toggleDay = (day) => {
    setWorkingDays(prev => prev.includes(day) ? prev.filter(d => d !== day) : [...prev, day]);
  };

  const handleTagSelect = (tag) => {
    setSelectedTags(prev => [...prev, tag]);
    setTagSearch('');
    setFilteredTags([]);
  };

  const handleTagRemove = (tag) => {
    setSelectedTags(prev => prev.filter(t => t.id !== tag.id));
  };

  const handleTagInputKeyDown = async (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const tagName = tagSearch.trim();
      if (tagName && !selectedTags.some(t => t.name.toLowerCase() === tagName.toLowerCase())) {
        // Check if tag exists in allTags
        const existingTag = allTags.find(t => t.name.toLowerCase() === tagName.toLowerCase());
        if (existingTag) {
          handleTagSelect(existingTag);
        } else {
          // For new tags, create a temporary object (will be handled by backend)
          const newTag = { id: null, name: tagName, temp: true };
          setSelectedTags(prev => [...prev, newTag]);
          setTagSearch('');
        }
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    // Formatting the "Hours" string for the database
    const hoursFormatted = workingDays.length > 0
      ? `${workingDays.join(', ')} (${timeRange.start}-${timeRange.end})`
      : 'Closed / Appointment Only';

    try {
      const payload = {
        name: formData.serviceName,
        description: formData.description,
        price: formData.price,
        pricing_type: formData.pricing_type,
        category_id: parseInt(formData.category),
        user_id: user.id,
        hours: hoursFormatted,
        tags: selectedTags, // Array of tag objects (with id and name)
      };

      if (formData.listingType === 'service') await createService(payload);
      else await createProduct(payload);

      alert('Listing created successfully!');
      navigate('/vendor-dashboard');
    } catch (err) {
      setError(err.message || 'Failed to submit');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="section" style={{ maxWidth: '720px' }}>
      <style>{`
        .day-pill { padding: 6px 12px; border: 1px solid var(--border); border-radius: 8px; cursor: pointer; font-size: 13px; font-weight: 600; transition: 0.2s; background: white; }
        .day-pill.active { background: var(--primary); color: white; border-color: var(--primary); }
        .tag-pill { padding: 6px 14px; border-radius: 20px; border: 1px solid var(--border); cursor: pointer; font-size: 12px; transition: 0.2s; background: var(--surface-2); }
        .tag-pill.active { background: var(--accent); color: white; border-color: var(--accent); }
        .suggestion-box { background: white; border: 1px solid var(--border); border-radius: 8px; max-height: 200px; overflow-y: auto; margin-bottom: 12px; }
        .suggestion-item { padding: 10px 12px; cursor: pointer; border-bottom: 1px solid var(--border); font-size: 13px; transition: 0.15s; background: white; }
        .suggestion-item:hover { background: var(--surface-2); }
        .suggestion-item:last-child { border-bottom: none; }
        .step-circle { flex: none; width: 28px; height: 28px; border-radius: 50%; background: var(--accent); display: flex; align-items: center; justify-content: center; color: white; fontSize: 0.75rem; fontWeight: 700; }
        .form-card { background: white; border: 1px solid var(--border); border-radius: 16px; padding: 24px; margin-bottom: 24px; }
      `}</style>

      <div style={{ textAlign: 'center', marginBottom: '32px' }}>
        <h1 style={{ fontFamily: 'var(--font-display)', fontSize: '2rem', fontWeight: 800 }}>Create a Listing</h1>
        <p style={{ color: 'var(--text-3)' }}>Fill in the details below to reach local customers.</p>
      </div>

      <form onSubmit={handleSubmit}>

        {/* SECTION 1: Category & Basic Info */}
        <div className="form-card">
          <div style={{ display: 'flex', gap: '12px', marginBottom: '20px' }}>
            <div className="step-circle">1</div>
            <div>
              <div style={{ fontWeight: 700, fontSize: '1.1rem' }}>Listing Details</div>
              <div style={{ fontSize: '0.85rem', color: 'var(--text-3)' }}>What are you offering?</div>
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">I want to list a...</label>
            <select className="form-select" value={formData.listingType} onChange={e => setFormData({ ...formData, listingType: e.target.value })}>
              <option value="service">Service (Plumber, Tutor, etc.)</option>
              <option value="product">Product (Handmade items, Food, etc.)</option>
            </select>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Category *</label>
              <CategorySelect
                categories={categories}
                value={formData.category}
                onChange={(catId) => setFormData({ ...formData, category: catId })}
                required
              />
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Listing Title *</label>
            <input type="text" className="form-input" placeholder="e.g. Professional Sofa Cleaning" value={formData.serviceName} onChange={e => setFormData({ ...formData, serviceName: e.target.value })} required />
          </div>
          <div className="form-group">
            <label className="form-label">Description *</label>
            <textarea className="form-textarea" rows="4" placeholder="Mention your expertise, experience, and what's included..." value={formData.description} onChange={e => setFormData({ ...formData, description: e.target.value })} required></textarea>
          </div>

          {/* Pricing Strategy Section */}
          <div style={{
            backgroundColor: '#f8fafc',
            border: '2px solid #e2e8f0',
            borderRadius: '12px',
            padding: '20px',
            marginTop: '20px'
          }}>
            <h4 style={{ marginTop: 0, marginBottom: '16px', color: '#1e293b' }}>
              💰 Pricing Strategy
            </h4>
            
            {/* Pricing Type Selection */}
            <div className="form-group" style={{ marginBottom: '20px' }}>
              <label className="form-label">Select Your Pricing Model *</label>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: '12px'
              }}>
                {[
                  { id: 'visiting', label: 'Visiting/Inspection Fee', icon: '🚗', description: 'Perfect for Electricians, Plumbers, AC Repair' },
                  { id: 'fixed', label: 'Fixed Rate', icon: '✅', description: 'Best for Salon, Car Wash, Tasks' },
                  { id: 'starting', label: 'Starting From', icon: '📈', description: 'Great for Painters, Carpenters, Projects' },
                  { id: 'subscription', label: 'Recurring/Subscription', icon: '🔄', description: 'Ideal for Milk, Tiffins, Tutors' },
                ].map(model => (
                  <div
                    key={model.id}
                    onClick={() => setFormData({ ...formData, pricing_type: model.id })}
                    style={{
                      padding: '12px',
                      border: formData.pricing_type === model.id ? '2px solid #4f46e5' : '2px solid #e2e8f0',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      backgroundColor: formData.pricing_type === model.id ? '#eef2ff' : '#fff',
                      transition: '0.2s',
                    }}
                  >
                    <div style={{ fontSize: '20px', marginBottom: '6px' }}>{model.icon}</div>
                    <div style={{
                      fontWeight: '600',
                      fontSize: '13px',
                      color: formData.pricing_type === model.id ? '#4f46e5' : '#1e293b',
                      marginBottom: '4px'
                    }}>
                      {model.label}
                    </div>
                    <div style={{ fontSize: '11px', color: '#64748b' }}>
                      {model.description}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Price Input with Dynamic Label */}
            <div className="form-group">
              <label className="form-label">
                {formData.pricing_type === 'visiting' && 'Visiting/Inspection Fee (₹) *'}
                {formData.pricing_type === 'fixed' && 'Fixed Price (₹) *'}
                {formData.pricing_type === 'starting' && 'Starting Price (₹) *'}
                {formData.pricing_type === 'subscription' && 'Subscription Rate (₹) *'}
              </label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontSize: '18px', fontWeight: '600', color: '#1e293b' }}>₹</span>
                <input
                  className="form-input"
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder={formData.pricing_type === 'subscription' ? 'e.g., 500 per month' : 'Enter price'}
                  value={formData.price}
                  onChange={e => setFormData({ ...formData, price: e.target.value })}
                  required
                  style={{ flex: 1 }}
                />
              </div>
              <div style={{ fontSize: '12px', color: '#64748b', marginTop: '6px' }}>
                {formData.pricing_type === 'visiting' && '✓ Charged per visit or inspection'}
                {formData.pricing_type === 'fixed' && '✓ One-time flat rate for the service'}
                {formData.pricing_type === 'starting' && '✓ Starting minimum for project-based work'}
                {formData.pricing_type === 'subscription' && '✓ Monthly or periodic recurring charge'}
              </div>
            </div>
          </div>
        </div>

        {/* SECTION 4: Working Hours */}
        <div className="form-card">
          <div style={{ display: 'flex', gap: '12px', marginBottom: '20px' }}>
            <div className="step-circle">2</div>
            <div>
              <div style={{ fontWeight: 700, fontSize: '1.1rem' }}>Working Hours</div>
              <div style={{ fontSize: '0.85rem', color: 'var(--text-3)' }}>When are you available?</div>
            </div>
          </div>

          <label className="form-label">Select Working Days</label>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '20px' }}>
            {daysList.map(day => (
              <div key={day} className={`day-pill ${workingDays.includes(day) ? 'active' : ''}`} onClick={() => toggleDay(day)}>
                {day}
              </div>
            ))}
          </div>

          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Opens At</label>
              <input type="time" className="form-input" value={timeRange.start} onChange={e => setTimeRange({ ...timeRange, start: e.target.value })} />
            </div>
            <div className="form-group">
              <label className="form-label">Closes At</label>
              <input type="time" className="form-input" value={timeRange.end} onChange={e => setTimeRange({ ...timeRange, end: e.target.value })} />
            </div>
          </div>
        </div>

        {/* SECTION 4: Tags (New Field) */}
        <div className="form-card">
          <div style={{ display: 'flex', gap: '12px', marginBottom: '20px' }}>
            <div className="step-circle">4</div>
            <div>
              <div style={{ fontWeight: 700, fontSize: '1.1rem' }}>Search Keywords</div>
              <div style={{ fontSize: '0.85rem', color: 'var(--text-3)' }}>Help customers find you via search</div>
            </div>
          </div>

          <label className="form-label">Tags (Optional - Press Enter to add)</label>
          <input
            type="text"
            className="form-input"
            placeholder="Search or type a tag and press Enter..."
            value={tagSearch}
            onChange={(e) => setTagSearch(e.target.value)}
            onKeyDown={handleTagInputKeyDown}
            style={{ marginBottom: '10px' }}
          />
          
          {filteredTags.length > 0 && (
            <div className="suggestion-box">
              {filteredTags.map(tag => (
                <div key={tag.id} onClick={() => handleTagSelect(tag)} className="suggestion-item">
                  {tag.name}
                </div>
              ))}
            </div>
          )}

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
            {selectedTags.map(tag => (
              <div key={tag.id || tag.name} className="tag-pill active">
                # {tag.name}
                <span
                  onClick={() => handleTagRemove(tag)}
                  style={{ marginLeft: '6px', cursor: 'pointer', fontWeight: 'bold' }}
                >
                  ✕
                </span>
              </div>
            ))}
          </div>
        </div>

        {error && <div style={{ color: 'red', marginBottom: '15px', textAlign: 'center' }}>{error}</div>}

          <button className="btn btn-primary btn-lg" style={{width: '100%', justifyContent: 'center', marginTop: '24px'}} type="submit" disabled={submitting}>
            {submitting ? '⏳ Submitting...' : '✓ Submit Listing for Free'}
        </button>
          <p style={{textAlign: 'center', fontSize: '0.78rem', color: 'var(--text-3)', marginTop: '10px'}}>
            By submitting you agree to our Terms of Service. Your listing will be live within 24 hours.
          </p>
      </form>
    </div>
  );
};

export default AddListing;